import React from 'react';
import { AlertTriangle, CheckCircle2, Inbox, RotateCw, X } from 'lucide-react';

/* --------------------------------------------------------------------------
   Estados de página compartilhados: vazio, erro (com "Tentar de novo"),
   sucesso, aviso assíncrono e progresso de ações que gastam créditos.
   Estilos em src/ui.css (tokens iguais aos de /explorar e /estudio).
   -------------------------------------------------------------------------- */

type StateProps = {
  icon?: React.ReactNode;
  title: string;
  text?: React.ReactNode;
  /** Botões/links da próxima ação. Use no máximo um `explore-btn is-primary`. */
  actions?: React.ReactNode;
  flat?: boolean;
  className?: string;
  /** Nível do título (use 1 quando o estado ocupa a página inteira). */
  headingLevel?: 1 | 2 | 3;
};

export function EmptyState({ icon, title, text, actions, flat, className = '', headingLevel }: StateProps) {
  const H = tagFor(headingLevel);
  return (
    <div className={`ui-state${flat ? ' is-flat' : ''} ${className}`.trim()}>
      <div className="ui-state-icon" aria-hidden>{icon ?? <Inbox size={24} />}</div>
      <H className="ui-state-title">{title}</H>
      {text && <p className="ui-state-text">{text}</p>}
      {actions && <div className="ui-state-actions">{actions}</div>}
    </div>
  );
}

type ErrorProps = {
  title?: string;
  message?: React.ReactNode;
  /** Detalhe técnico (mensagem original), mostrado discreto. */
  detail?: string | null;
  onRetry?: () => void;
  retrying?: boolean;
  retryLabel?: string;
  /** Ação secundária opcional (ex.: link para suporte). */
  actions?: React.ReactNode;
  flat?: boolean;
  headingLevel?: 1 | 2 | 3;
};

type HeadingTag = 'h1' | 'h2' | 'h3';
const tagFor = (level: 1 | 2 | 3 = 2) => `h${level}` as HeadingTag;

export function ErrorState({
  title = 'Não foi possível carregar',
  message = 'Confira sua conexão e tente de novo. Nada foi cobrado.',
  detail,
  onRetry,
  retrying,
  retryLabel = 'Tentar de novo',
  actions,
  flat,
  headingLevel,
}: ErrorProps) {
  const H = tagFor(headingLevel);
  return (
    <div className={`ui-state is-error${flat ? ' is-flat' : ''}`} role="alert">
      <div className="ui-state-icon" aria-hidden><AlertTriangle size={24} /></div>
      <H className="ui-state-title">{title}</H>
      {message && <p className="ui-state-text">{message}</p>}
      {detail && <p className="ui-state-detail">{detail}</p>}
      {(onRetry || actions) && (
        <div className="ui-state-actions">
          {onRetry && (
            <button type="button" className="explore-btn is-primary" onClick={onRetry} disabled={retrying}>
              <RotateCw size={16} aria-hidden />
              {retrying ? 'Tentando…' : retryLabel}
            </button>
          )}
          {actions}
        </div>
      )}
    </div>
  );
}

export function SuccessState({ icon, title, text, actions, flat, className = '', headingLevel }: StateProps) {
  const H = tagFor(headingLevel);
  return (
    <div className={`ui-state is-success${flat ? ' is-flat' : ''} ${className}`.trim()} role="status">
      <div className="ui-state-icon" aria-hidden>{icon ?? <CheckCircle2 size={24} />}</div>
      <H className="ui-state-title">{title}</H>
      {text && <p className="ui-state-text">{text}</p>}
      {actions && <div className="ui-state-actions">{actions}</div>}
    </div>
  );
}

type NoticeProps = {
  tone?: 'info' | 'error' | 'success' | 'warn';
  children: React.ReactNode;
  actions?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  /** Mostra o botão Dispensar aviso. */
  onDismiss?: () => void;
};

/** Aviso inline anunciado por leitor de tela (erro = assertive, resto = polite). */
export function Notice({ tone = 'info', children, actions, icon, className = '', onDismiss }: NoticeProps) {
  const defaultIcon = tone === 'success' ? <CheckCircle2 size={18} aria-hidden /> : <AlertTriangle size={18} aria-hidden />;
  return (
    <div
      className={`ui-notice${tone === 'info' ? '' : ` is-${tone}`} ${className}`.trim()}
      role={tone === 'error' ? 'alert' : 'status'}
      aria-live={tone === 'error' ? 'assertive' : 'polite'}
    >
      {icon ?? defaultIcon}
      <div className="ui-notice-body">
        {children}
        {actions && <div className="ui-notice-actions">{actions}</div>}
      </div>
      {onDismiss && (
        <button type="button" className="ui-notice-dismiss" onClick={onDismiss} aria-label="Dispensar aviso">
          <X size={16} aria-hidden />
        </button>
      )}
    </div>
  );
}

export type Feedback = { type: 'success' | 'error'; message: string } | null;

/** Atalho para o padrão eedback das telas de gestão. */
export function FeedbackNotice({ feedback, onDismiss }: { feedback: Feedback; onDismiss: () => void }) {
  if (!feedback) return null;
  return (
    <Notice tone={feedback.type} onDismiss={onDismiss} className="ui-notice-spaced">
      {feedback.message}
    </Notice>
  );
}

type ProgressProps = {
  label: string;
  /** Custo já debitado/reservado, ex. "18 créditos". Só exibição. */
  cost?: string;
  detail?: React.ReactNode;
  /** 0–1. Sem valor = indeterminado. */
  value?: number;
};

/** Progresso visível logo após uma ação que gasta créditos (não só toast). */
export function CreditProgress({ label, cost, detail, value }: ProgressProps) {
  const determinate = typeof value === 'number';
  return (
    <div className={`ui-progress${determinate ? '' : ' is-indeterminate'}`} role="status" aria-live="polite">
      <div className="ui-progress-head">
        <span>{label}</span>
        {cost && <span className="ui-progress-cost">{cost}</span>}
      </div>
      <div
        className="ui-progress-track"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={determinate ? Math.round(Math.min(1, Math.max(0, value!)) * 100) : undefined}
      >
        <div className="ui-progress-fill" style={determinate ? { transform: `scaleX(${Math.min(1, Math.max(0, value!))})` } : undefined} />
      </div>
      {detail && <p className="ui-progress-detail">{detail}</p>}
    </div>
  );
}

/* ----------------------------- Skeletons --------------------------------- */

type SkelProps = { w?: number | string; h?: number | string; className?: string; style?: React.CSSProperties };

/** Bloco com shimmer. Decorativo: o anúncio fica no wrapper `SkeletonRegion`. */
export function Skel({ w = '100%', h = 14, className = 'sk-line', style }: SkelProps) {
  return <span className={`sk ${className}`} style={{ width: w, height: h, ...style }} aria-hidden />;
}

/** Envolve qualquer skeleton: anuncia "Carregando…" uma única vez para leitor de tela. */
export function SkeletonRegion({ label = 'Carregando…', children }: { label?: string; children: React.ReactNode }) {
  return (
    <div role="status" aria-busy="true" aria-live="polite">
      <span className="sr-only">{label}</span>
      <div aria-hidden>{children}</div>
    </div>
  );
}

export function SkeletonHeader({ withSubtitle = true }: { withSubtitle?: boolean }) {
  return (
    <div className="page-header" aria-hidden>
      <div className="sk-stack" style={{ width: '100%' }}>
        <Skel w={220} h={30} />
        {withSubtitle && <Skel w={320} h={14} />}
      </div>
    </div>
  );
}

export function SkeletonStats({ count = 4 }: { count?: number }) {
  return (
    <div className="sk-stats">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="sk-stat">
          <Skel className="sk-circle" w={48} h={48} />
          <div className="sk-stack" style={{ flex: 1 }}>
            <Skel w="55%" h={11} />
            <Skel w="35%" h={22} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SkeletonList({ rows = 5, thumb = true }: { rows?: number; thumb?: boolean }) {
  return (
    <div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="sk-row">
          {thumb && <Skel className="" w={48} h={48} style={{ borderRadius: 8, flexShrink: 0 }} />}
          <div className="sk-row-body">
            <Skel w={`${70 - (i % 3) * 12}%`} />
            <Skel w="28%" h={10} />
          </div>
          <Skel w={72} h={24} style={{ borderRadius: 999 }} />
        </div>
      ))}
    </div>
  );
}

export function SkeletonTable({ rows = 6, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="sk-row">
          {Array.from({ length: cols }).map((__, c) => (
            <Skel key={c} w={c === 0 ? '30%' : `${Math.max(12, 24 - c * 3)}%`} h={14} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function SkeletonCards({ count = 8, ratio = '4 / 3' }: { count?: number; ratio?: string }) {
  return (
    <div className="sk-cards">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="sk-tile">
          <span className="sk" style={{ aspectRatio: ratio, borderRadius: 0 }} />
          <div className="sk-tile-body">
            <Skel w="72%" />
            <Skel w="40%" h={10} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SkeletonPanel({ lines = 4, height }: { lines?: number; height?: number }) {
  return (
    <div className="sk-panel">
      <div className="sk-stack">
        <Skel w="35%" h={18} />
        {height ? <Skel h={height} style={{ borderRadius: 12 }} /> : Array.from({ length: lines }).map((_, i) => <Skel key={i} w={`${95 - i * 10}%`} />)}
      </div>
    </div>
  );
}
